import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  IdentityProfile,
  ProfileOrg,
  RefreshRecoveryKeyRequest,
  SecurityCompliance,
  SecurityComplianceQuery,
  TfaInfo,
  TfaInfoQuery,
  TfaService
} from '@b3networks/api/auth';
import { SessionQuery, SessionService } from '@b3networks/portal/base/shared';
import { DestroySubscriberComponent } from '@b3networks/shared/common';
import { ConfirmDialogComponent, ConfirmDialogInput } from '@b3networks/shared/ui/confirm-dialog';
import { ToastService } from '@b3networks/shared/ui/toast';
import { filter, takeUntil } from 'rxjs/operators';
import { EmailModalSettingsComponent } from './modal/email-modal.component';
import { PasswordModalSettingsComponent } from './modal/password-modal.component';
import { TwoFaActionType } from './security.model';
import { AuthenticatorAppModal } from './twofa/authenticatorApp/authenticatorApp.component';
import { DeleteAuthenticatorAppModal } from './twofa/authenticatorApp/confirmDelete.component';
import { EnableTwofaV2Component, EnableTwofaV2Data } from './twofa/enable-twofa-v2/enable-twofa-v2.component';
import { RecoveryKeyComponent } from './twofa/enable-twofa-v2/recovery-key/recovery-key.component';
import { TwofaModalComponent } from './twofa/twofa-modal';

@Component({
  selector: 'b3n-account-security-settings',
  templateUrl: 'security.component.html',
  styleUrls: ['security.component.scss']
})
export class SecuritySettingsComponent extends DestroySubscriberComponent implements OnInit {
  profile: IdentityProfile;
  currentOrg: ProfileOrg;
  tfaInfo: TfaInfo;
  securityCompliance: SecurityCompliance;

  constructor(
    private sessionQuery: SessionQuery,
    private sessionSerice: SessionService,
    private tfaInfoQuery: TfaInfoQuery,
    private securityComplianceQuery: SecurityComplianceQuery,
    private dialog: MatDialog,
    private tfaService: TfaService,
    private toastService: ToastService
  ) {
    super();
  }

  ngOnInit() {
    this.sessionQuery.profile$.pipe(takeUntil(this.destroySubscriber$)).subscribe(profile => {
      if (profile && profile.uuid) {
        this.profile = profile;
      }
    });
    this.sessionQuery.currentOrg$
      .pipe(
        takeUntil(this.destroySubscriber$),
        filter(org => org != null)
      )
      .subscribe(currentOrg => {
        if (currentOrg) {
          this.currentOrg = currentOrg;
        }
      });
    this.tfaInfoQuery.tfaInfo$.pipe(takeUntil(this.destroySubscriber$)).subscribe((response: TfaInfo) => {
      this.tfaInfo = response;
    });
    this.securityComplianceQuery.securityCompliance$
      .pipe(takeUntil(this.destroySubscriber$))
      .subscribe((response: SecurityCompliance) => {
        this.securityCompliance = response;
      });
  }

  setup2fa() {
    const profile = this.sessionQuery.profile;
    this.dialog
      .open(EnableTwofaV2Component, {
        width: '560px',
        disableClose: true,
        data: <EnableTwofaV2Data>{
          email: profile.email,
          tfaInfo: this.tfaInfo
        }
      })
      .afterClosed()
      .subscribe(needToFetch => {
        if (needToFetch) {
          this.tfaService.get2FaInfo().subscribe();
        }
      });
  }

  disable2fa() {
    if (this.tfaInfo.defaultMethod === 'EMAIL') {
      this.disable2faUsingEmail();
    } else {
      this.disable2faUsingAuthenticator();
    }
  }

  disable2faUsingEmail() {
    this.dialog
      .open(TwofaModalComponent, {
        width: '600px',
        autoFocus: false,
        data: {
          email: this.profile.email,
          tfaInfo: this.tfaInfo,
          action: TwoFaActionType.DISABLE_TFA
        }
      })
      .afterClosed()
      .subscribe(() => {
        this.tfaService.get2FaInfo().subscribe();
      });
  }

  disable2faUsingAuthenticator() {
    this.dialog
      .open(ConfirmDialogComponent, {
        data: <ConfirmDialogInput>{
          title: 'Disable two-factor authentication',
          message: 'Two-factor verification makes your account more secure. Are you sure you want to disable it?',
          confirmLabel: 'Disable',
          cancelLabel: 'Cancel',
          color: 'warn'
        },
        width: '560px'
      })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.tfaService.toggle2Fa({}).subscribe({
            next: () => {
              this.tfaService.get2FaInfo().subscribe();
              this.toastService.success('Updated sucessfully');
            },
            error: () => {
              this.toastService.error('Failed to disable two-factor authentication');
            }
          });
        }
      });
  }

  createNewRecoveryCode() {
    this.tfaInfo.defaultMethod === 'EMAIL' ? this.openRecoveryCodeModal() : this.openConfirmDialog();
  }

  openRecoveryCodeModal() {
    this.dialog
      .open(TwofaModalComponent, {
        width: '600px',
        data: {
          email: this.profile.email,
          tfaInfo: this.tfaInfo,
          action: TwoFaActionType.CREATE_RECOVERY_KEY
        }
      })
      .afterClosed()
      .subscribe(() => {
        this.tfaService.get2FaInfo().subscribe();
      });
  }

  openConfirmDialog() {
    this.dialog
      .open(ConfirmDialogComponent, {
        data: <ConfirmDialogInput>{
          title: 'Create new recovery code',
          message: 'Are you sure you want to create a new recovery code?',
          confirmLabel: 'Create',
          cancelLabel: 'Cancel',
          color: 'primary'
        },
        width: '560px'
      })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.refreshRecoveryCode();
        }
      });
  }

  refreshRecoveryCode() {
    this.tfaService.refreshRecoveryCode({} as RefreshRecoveryKeyRequest).subscribe({
      next: resp => {
        this.dialog.open(RecoveryKeyComponent, {
          data: resp.recoveryKey, // new key
          width: '600px'
        });
        this.tfaService.get2FaInfo().subscribe();
      },
      error: () => {
        this.toastService.error('Failed to refresh recovery key');
      }
    });
  }

  swich2fa() {
    if (this.tfaInfo.tfaEnabled) {
      this.disable2fa();
    } else {
      this.setup2fa();
    }
  }

  deleteTotpSuccessful() {
    this.tfaInfo.totpActivated = false;
  }

  setupAuthenticatorSuccessful() {
    this.tfaInfo.totpActivated = true;
  }

  showEmailModal() {
    const dialogRef = this.dialog.open(EmailModalSettingsComponent, {
      width: '600px',
      data: {
        email: this.profile.email,
        unverifiedEmail: this.profile.unverifiedEmail,
        unverifiedEmailToken: this.profile.unverifiedEmailToken
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.success) {
        this.sessionSerice.getProfile().subscribe(profile => {
          this.profile = profile;
        });
      }
    });
  }

  showPasswordModal() {
    this.dialog.open(PasswordModalSettingsComponent, {
      width: '600px',
      data: this.securityCompliance['passwordPolicy'] || null
    });
  }

  showAuthenticatorModal() {
    this.dialog.open(AuthenticatorAppModal, {
      width: '600px'
    });
  }

  showDeleteAuthenticationModal() {
    this.dialog.open(DeleteAuthenticatorAppModal, {
      width: '600px'
    });
  }
}
